import React, { useEffect, useState, useCallback } from 'react';
import './PrescriptionsListTable.css';
import axios from 'axios';
import ENDPOINTS from '../../Constants/ApiEndpoints';
import { dateTimeFormatter } from '../../CommonComponents/Utils/DateTimeFormatter';
import { Box, Pagination, PaginationItem } from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const INVOICE_TYPES = ['Purchase', 'Return'];

const STATUS_MAP = {
  PAID: 'Completed',
  UNPAID: 'Pending',
  PARTIALLY_PAID: 'Partially Paid',
  SAVED: 'Saved',
  DELETED: 'Deleted',
  COMPLETED_IN_IP: 'Completed in IP',
};

const STATUS_TYPES = Object.values(STATUS_MAP);

export default function PrescriptionsListTable({ startDate, endDate, onSelectPatient, prescriptionFilters }) {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showPatientFilter, setShowPatientFilter] = useState(false);
  const [patientFilter, setPatientFilter] = useState('');

  const [showInvoiceFilter, setShowInvoiceFilter] = useState(false);
  const [selectedInvoiceTypes, setSelectedInvoiceTypes] = useState([]);

  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const [waitTimeSortOrder, setWaitTimeSortOrder] = useState(null); // 'asc' | 'desc' | null
  const [activeTab, setActiveTab] = useState('Walkin Requests');


  //pagination begins
  const [page, setPage] = useState(0); // Spring pagination is 0-based
  const [pageSize, setPageSize] = useState(10); // Or default page size from backend
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [counts, setCounts] = useState(0);
  //pagination ends
  const tabStatusMap = {
    "Walkin Requests": "STANDALONE_PRESCRIPTION",
    "OP Requests": "OP_PRESCRIPTION",
    "IP Requests": "IP_PRESCRIPTION"
  };
  
  const mappedTabStatus = tabStatusMap[activeTab]; // Use this in the API call



  const formatDate = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };
  // Fetch data from API with all filters (date + prescriptionFilters)
  const fetchData = async (currentPage = page, currentSize = pageSize) => {
    setLoading(true);
    try {
      const params = {
        fromDate: formatDate(startDate),
        toDate: formatDate(endDate),
        prescriptionType:mappedTabStatus,
        page: currentPage,
        size: currentSize,
      };
      // Add prescriptionFilters if present
      if (prescriptionFilters?.type && prescriptionFilters?.query) {
        if (prescriptionFilters.type === 'MR No') params.mrNumber = prescriptionFilters.query;
        if (prescriptionFilters.type === 'Name') params.name = prescriptionFilters.query;
        if (prescriptionFilters.type === 'Phone') params.phoneNumber = prescriptionFilters.query;
      }

      const response = await axios.get(ENDPOINTS.SEARCH_PRESCRIPTIONS_BY_FILTERS, { params });
      setRecords(response.data.records);
      setCounts({
        walkinPrescriptions: response.data.walkinPrescriptions,
        opPrescriptions: response.data.opPrescriptions,
        ipPrescriptions: response.data.ipPrescriptions,
      });

      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error) {
      console.error('Error fetching Admissions data:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, prescriptionFilters]);


  useEffect(() => {
    setPage(0); // reset to first page when tab changes
  }, [activeTab]);

  useEffect(() => {
    fetchData(page, pageSize); // refetch whenever page or activeTab changes
  }, [page, pageSize, activeTab]);


  useEffect(() => {
    fetchData(page, pageSize); // ✅ always send correct page
    // Smooth scroll to top of table
    const tableTop = document.querySelector('.table-container')?.offsetTop;
    if (tableTop !== undefined) {
      window.scrollTo({ top: tableTop - 100, behavior: 'smooth' });
    }
  }, [page, pageSize]); // 👈 This triggers fetchData when page or size changes



  useEffect(() => {
    let filtered = [...records];

    if (patientFilter.trim() !== '') {
      filtered = filtered.filter((r) =>
        r.patientName?.toLowerCase().includes(patientFilter.toLowerCase())
      );
    }

    if (selectedInvoiceTypes.length > 0) {
      filtered = filtered.filter((r) => {
        const itemType = r.invoiceType || r.type || 'Purchase';
        return selectedInvoiceTypes.includes(itemType);
      });
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((r) =>
        selectedStatuses.includes(STATUS_MAP[r.invoiceStatus] || r.invoiceStatus)
      );
    }

    // Filter based on active tab
    if (activeTab === "Walkin Requests") {
      filtered = filtered.filter(r => r.prescriptionType === 'STANDALONE_PRESCRIPTION');
    } else if (activeTab === "OP Requests") {
      filtered = filtered.filter(r => r.prescriptionType === 'OP_PRESCRIPTION');
    } else if (activeTab === "IP Requests") {
      filtered = filtered.filter(r => r.prescriptionType === 'IP_PRESCRIPTION');
    }

    // Sort by wait time
    if (waitTimeSortOrder) {
      filtered.sort((a, b) => {
        const wtA = parseFloat(a.waitTime) || 0;
        const wtB = parseFloat(b.waitTime) || 0;
        return waitTimeSortOrder === 'asc' ? wtA - wtB : wtB - wtA;
      });
    }

    setFilteredRecords(filtered);
  }, [records, patientFilter, selectedInvoiceTypes, selectedStatuses, waitTimeSortOrder, activeTab]);

  const toggleSelection = useCallback((value, current, setter) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  }, []);

  const toggleWaitTimeSort = () => {
    setWaitTimeSortOrder((prev) =>
      prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'
    );
  };

  const walkinRequestsCount = records.filter(r => r.prescriptionType === 'STANDALONE_PRESCRIPTION').length;
  const opRequestsCount = records.filter(r => r.prescriptionType === 'OP_PRESCRIPTION').length;
  const ipRequestsCount = records.filter(r => r.prescriptionType === 'IP_PRESCRIPTION').length;

  const handleDeleteSavedDraft = async (record) => {
    if (!window.confirm(`Are you sure you want to delete the saved prescription draft for ${record.patientName || record.mrNumber || 'this patient'}?`)) {
      return;
    }
    try {
      await axios.delete(`${ENDPOINTS.DELETE_PRESCRIPTION}/${record.prescriptionId}`);
      setRecords((prev) =>
        prev.map((r) =>
          r.prescriptionId === record.prescriptionId
            ? { ...r, prescriptionStatus: 'DELETED', invoiceStatus: 'DELETED', isDeleted: true }
            : r
        )
      );
    } catch (err) {
      console.error("Failed to delete draft:", err);
      toast.error("Failed to delete draft prescription.");
    }
  };

  const completedCount = filteredRecords.filter(r => r.invoiceStatus === 'PAID' && r.prescriptionStatus !== 'DELETED').length;
  const pendingCount = filteredRecords.filter(r => (r.invoiceStatus === 'UNPAID' || r.invoiceStatus === 'PARTIALLY_PAID') && r.prescriptionStatus !== 'DELETED').length;
  const deletedCount = filteredRecords.filter(r => r.invoiceStatus === 'DELETED' || r.prescriptionStatus === 'DELETED' || r.isDeleted).length;
  const savedCount = filteredRecords.filter(r => r.prescriptionStatus === 'SAVED').length;

  return (
    <div className="walkin-container">
      <div className="pharmacy-master-card">
        {/* Top Accent Bar */}
        <div className="pharmacy-card-accent-bar" />

        {/* Toolbar Header: Tab Pills & Status Stats Counter Chips */}
        <div className="pharmacy-toolbar-header">
          <div className="pharmacy-tabs-pills">
            <button
              className={`pharmacy-tab-pill ${activeTab === "Walkin Requests" ? "active-tab" : ""}`}
              onClick={() => setActiveTab("Walkin Requests")}
            >
              Walkin Requests <span className="tab-count-badge">{counts.walkinPrescriptions || 0}</span>
            </button>
            <button
              className={`pharmacy-tab-pill ${activeTab === "OP Requests" ? "active-tab" : ""}`}
              onClick={() => setActiveTab("OP Requests")}
            >
              OP Requests <span className="tab-count-badge">{counts.opPrescriptions || 0}</span>
            </button>
            <button
              className={`pharmacy-tab-pill ${activeTab === "IP Requests" ? "active-tab" : ""}`}
              onClick={() => setActiveTab("IP Requests")}
            >
              IP Requests <span className="tab-count-badge">{counts.ipPrescriptions || 0}</span>
            </button>
          </div>

          <div className="status-stats-group">
            {savedCount > 0 && (
              <span className="status-stat-chip chip-slate">
                <span>Saved Drafts</span> <strong>{savedCount}</strong>
              </span>
            )}
            <span className="status-stat-chip chip-green">
              <span>Completed</span> <strong>{completedCount}</strong>
            </span>
            <span className="status-stat-chip chip-amber">
              <span>Pending</span> <strong>{pendingCount}</strong>
            </span>
            <span className="status-stat-chip chip-red">
              <span>Deleted</span> <strong>{deletedCount}</strong>
            </span>
          </div>
        </div>

        {/* Table responsive container */}
        <div className="pharmacy-table-responsive">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <p>Loading Prescriptions...</p>
            </div>
          ) : (
            <table className="pharmacy-data-table">
              <thead>
                <tr>
                  <th onClick={toggleWaitTimeSort} style={{ cursor: 'pointer' }}>
                    Date & Time {waitTimeSortOrder === 'asc' ? '↑' : waitTimeSortOrder === 'desc' ? '↓' : ''}
                  </th>

                  <th style={{ position: 'relative' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => setShowPatientFilter((prev) => !prev)}>
                      PATIENT NAME <i className="fas fa-filter" style={{ color: '#64748b', fontSize: '11px' }} />
                    </div>
                    {showPatientFilter && (
                      <div className="pharmacy-filter-popover" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={patientFilter}
                          onChange={(e) => setPatientFilter(e.target.value)}
                          placeholder="Search patient..."
                          autoFocus
                        />
                      </div>
                    )}
                  </th>

                  <th>MR NUMBER</th>

                  {/* Invoice type filter */}
                  <th style={{ position: 'relative' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => setShowInvoiceFilter((prev) => !prev)}>
                      INVOICE TYPE <i className="fas fa-filter" style={{ color: '#64748b', fontSize: '11px' }} />
                    </div>
                    {showInvoiceFilter && (
                      <div className="pharmacy-filter-popover" onClick={(e) => e.stopPropagation()}>
                        {INVOICE_TYPES.map((type) => (
                          <label key={type}>
                            <input
                              type="checkbox"
                              checked={selectedInvoiceTypes.includes(type)}
                              onChange={() => toggleSelection(type, selectedInvoiceTypes, setSelectedInvoiceTypes)}
                            /> {type}
                          </label>
                        ))}
                      </div>
                    )}
                  </th>

                  <th>ITEMS</th>

                  <th style={{ position: 'relative' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => setShowStatusFilter((prev) => !prev)}>
                      STATUS <i className="fas fa-filter" style={{ color: '#64748b', fontSize: '11px' }} />
                    </div>
                    {showStatusFilter && (
                      <div className="pharmacy-filter-popover" onClick={(e) => e.stopPropagation()}>
                        {STATUS_TYPES.map((status) => (
                          <label key={status}>
                            <input
                              type="checkbox"
                              checked={selectedStatuses.includes(status)}
                              onChange={() => toggleSelection(status, selectedStatuses, setSelectedStatuses)}
                            /> {status}
                          </label>
                        ))}
                      </div>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="pharmacy-empty-cell">
                      No prescriptions found matching criteria.
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record, index) => {
                    const isFlagged = Boolean(record.flagged || record.isFlagged || record.patientFlagged);
                    const isEmergency = Boolean(record.emergency || record.isEmergency);
                    let rowClass = "pharmacy-table-row";
                    if (isFlagged) rowClass += " flagged-row";
                    else if (isEmergency) rowClass += " emergency-row";

                    return (
                      <tr key={index} onClick={() => onSelectPatient(record)} className={rowClass}>
                        <td>{dateTimeFormatter(record.prescriptionDate, record.prescriptionTime)}</td>
                        <td style={{ fontWeight: 600 }}>{record.patientName}</td>
                        <td>
                          <span className="mr-number-pill">{record.mrNumber}</span>
                        </td>
                        <td>
                          <span className={`type-pill ${(record.invoiceType || record.type || 'Purchase').toLowerCase() === 'return' ? 'pill-return' : 'pill-purchase'}`}>
                            {record.invoiceType || record.type || 'Purchase'}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{record.medicineCount}</td>
                        <td>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <span className={`status-chip-badge ${
                              record.prescriptionStatus === 'SAVED' ? 'chip-saved' :
                              record.prescriptionStatus === 'DELETED' || record.invoiceStatus === 'DELETED' ? 'chip-deleted' :
                              record.invoiceStatus === 'PAID' ? 'chip-paid' :
                              record.invoiceStatus === 'PARTIALLY_PAID' ? 'chip-partially' :
                              record.invoiceStatus === 'UNPAID' ? 'chip-unpaid' : 'chip-deleted'
                            }`}>
                              {record.prescriptionStatus === 'DELETED' ? 'Deleted' : record.prescriptionStatus === 'SAVED' ? 'Saved (Draft)' : (STATUS_MAP[record.invoiceStatus] || record.invoiceStatus || 'Draft')}
                            </span>
                            {record.prescriptionStatus === 'SAVED' && (
                              <button
                                className="table-trash-btn"
                                title="Delete saved draft"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSavedDraft(record);
                                }}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Pagination
          count={totalPages}
          page={page + 1}
          onChange={(e, value) => setPage(value - 1)}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              components={{
                previous: () => <span>Previous</span>,
                next: () => <span>Next</span>,
              }}
            />
          )}
          color="primary"
          variant="outlined"
          shape="rounded"
        />
      </Box>
    </div>
  );
}
